'use client';
import { useState } from 'react';
import Image from 'next/image';

import { VillageProductReview } from '@/services/types';
import { cn } from '@/lib/utils';
import ReviewsSectionRating from './Rating';
import { IVillageDictionary } from '@/dictionaries/pages/village/types';

import logoMobile from '../../assets/images/logo/logo-mobile.svg';
import trustpilotLogo from '../../assets/images/review/trustpilot.svg';

import './styles.css';

type Props = {
  dict: IVillageDictionary;
  className?: string;
  review: VillageProductReview;
  lang: string;
  showResponse?: string | null;
};

export default function ReviewsSectionItem({
  dict,
  review,
  className,
  lang,
  showResponse,
}: Props) {
  const [showContent, setShowContent] = useState(false);
  const reviewDate = new Date(review.createdAt);
  const { readAll, readLess, customerService } = dict.reviewsSection;

  reviewDate.setHours(reviewDate.getHours() + 3);

  const monthName = new Intl.DateTimeFormat(lang, { month: 'long' }).format(
    reviewDate,
  );

  const reviewYear = reviewDate.getFullYear();
  const commentIsTooLong = review.content.length > 700;

  return (
    <div className={className}>
      <div className="flex flex-wrap">
        <div className="w-full flex-[0_0_auto] desktop:w-1/6">
          <h3 className="mb-4 text-body font-semibold">{review.authorName}</h3>
          <div className="text-gray-700">
            <p className="capitalize mb-4 leading-[1.6]">{`${monthName} ${reviewYear}`}</p>
            <Image
              width={0}
              height={0}
              className="w-[100px] my-4 inline-block align-middle h-auto"
              src={review.isTrustpilot ? trustpilotLogo : logoMobile}
              alt="Logo"
            />
          </div>
        </div>
        <div className="m-0 p-0 desktop:w-5/6">
          <div className="flex flex-col flex-wrap mb-4">
            <ReviewsSectionRating
              className="text-primary mb-2 mr-4"
              averageScore={
                !!review.externalServiceScore && review.reviewOnline === 'tr'
                  ? review.externalServiceScore!
                  : review.score
              }
            />
            <span className="font-semibold text-xl">
              {!!review.externalServiceTitle && review.reviewOnline === 'tr'
                ? review.externalServiceTitle
                : review.title}
            </span>
          </div>
          <p
            className={cn(
              'relative mb-4 leading-[1.6]',
              {
                "content-display max-h-[150px] overflow-hidden transition-[max-height] duration-200 ease-in-out after:content-[''] after:h-[50px] after:absolute after:bottom-0 after:left-0 after:w-full":
                  commentIsTooLong,
              },
              {
                'max-h-full overflow-auto after:h-0':
                  commentIsTooLong && showContent,
              },
            )}>
            {!!review.externalServiceContent && review.reviewOnline === 'tr'
              ? review.externalServiceContent
              : review.adminContent || review.content}
          </p>
          {commentIsTooLong && (
            <strong
              onClick={() => setShowContent(!showContent)}
              className="uppercase cursor-pointer font-semibold text-blue">
              {showContent ? readLess : readAll}
            </strong>
          )}
          {showResponse && (
            <div className="relative mt-[30px] pl-[10%] before:content-[''] before:absolute before:left-[5%] before:h-full before:top-[0] before:w-[2px] before:bg-gray-200">
              <h3 className="text-body mb-2 text-gray-700 font-semibold">
                {customerService}
              </h3>
              <p className="mb-4 text-gray-700">{showResponse}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
